import isNil from 'lodash/isNil';

import { PeriodOptions } from '../../../models';

export class DateTimeService {
	TIMEZONE = 'Europe/Paris';

	public parseJsonWithDateStrings<T>(rawData: string): T {
		const dateTimeCreator = (_: any, value: any) => {
			if (typeof value !== 'string') return value;
			const isISOString = this.isIsoDateString(value);

			if (isISOString) return new Date(value);
			return value;
		};
		return JSON.parse(rawData, dateTimeCreator) as T;
	}

	public getReadableDate(date: Date | undefined): {
		calendar: string;
		clock: string;
	} {
		const display = { calendar: '00/00/0000', clock: '00:00' };
		if (typeof date === 'undefined') return display;
		const parts = new Intl.DateTimeFormat('fr-FR', {
			day: '2-digit',
			hour: '2-digit',
			hour12: false,
			minute: '2-digit',
			month: '2-digit',
			timeZone: this.TIMEZONE,
			year: 'numeric',
		}).formatToParts(date);
		const get = (type: string) =>
			parts.find((p) => p.type === type)?.value ?? '00';
		const calendar = `${get('day')}/${get('month')}/${get('year')}`;
		const clock = `${get('hour')}:${get('minute')}`;
		return { calendar, clock };
	}

	public addOrSubtractMonthsToDate = (
		date: Date,
		months: number,
		addition?: 'addition'
	) => {
		let changedDate = new Date(date);
		changedDate = addition
			? new Date(changedDate.setMonth(date.getMonth() + months))
			: new Date(changedDate.setMonth(date.getMonth() - months));
		return changedDate;
	};

	public getHistoryDate = (period: PeriodOptions) => {
		const { TODAY, WEEK, LAST_MONTH_START_DAY, LAST_MONTH_END_DAY } =
			PeriodOptions;
		const today = new Date();
		today.setHours(0, 0, 0);
		let usedDate = new Date();
		if (period === TODAY) {
			usedDate = new Date();
			usedDate.setHours(23, 59, 59, 999);
		} else if (period === WEEK) {
			usedDate = new Date(today.setDate(today.getDate() - 7));
			usedDate.setHours(0, 0, 0);
		} else if (period === LAST_MONTH_START_DAY) {
			// prevStartDate
			usedDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
			usedDate.setHours(0, 0, 0);
		} else if (period === LAST_MONTH_END_DAY) {
			// prevEndDate
			usedDate = new Date(today.getFullYear(), today.getMonth() - 1 + 1, 0);
			usedDate.setHours(23, 59, 59, 999);
		}
		return usedDate;
	};

	public getSyncDateString(date: Date): string[] {
		return [
			this.getTime(date).replace('.', ':'),
			this.getDate(date).slice(0, 5),
		];
	}

	public dayDiff(d1: Date | undefined, d2: Date): string | undefined {
		if (isNil(d1) || isNil(d2)) return undefined;
		const diff = Math.abs(d1.getTime() - d2.getTime());
		return Math.floor(diff / (1000 * 3600 * 24)).toString();
	}

	public getRelativeDate(
		date: Date,
		param: { year?: number; month?: number; day?: number },
		todayMax = false
	) {
		const newDate = new Date(
			date.getFullYear() + (param.year ?? 0),
			date.getMonth() + (param.month ?? 0),
			date.getDate() + (param.day ?? 0),
			date.getHours(),
			date.getMinutes()
		);
		if (todayMax && newDate > new Date()) return new Date();
		return newDate;
	}

	public isBetween(givenHour: number, lowerHour = 22, upperHour = 5): boolean {
		if (lowerHour < upperHour)
			return givenHour >= lowerHour && givenHour < upperHour;
		else if (lowerHour === upperHour) return givenHour === lowerHour;
		else return givenHour >= lowerHour || givenHour <= upperHour;
	}
	/**
	 * Get local date in DD/MM/YYYY format
	 * @param date
	 * @param separator
	 */
	public getDate(date: Date | undefined, separator = '/'): string {
		if (isNil(date)) return '';
		const day = ('0' + date.getDate()).slice(-2);
		const month = ('0' + (date.getMonth() + 1)).slice(-2);
		return `${day}${separator}${month}${separator}${date.getFullYear()}`;
	}

	public getDateYYYYMMDD(date: Date) {
		return date.toLocaleDateString('fr-CA');
	}

	/**
	 * Get local time in HH.MM format
	 */
	public getTime(date: Date | undefined): string {
		if (isNil(date)) return '';
		return date
			? `${('0' + date.getHours()).slice(-2)}:${('0' + date.getMinutes()).slice(
					-2
				)}`
			: '00:00';
	}

	getCurrentMonthString() {
		const monthNames = [
			'Janvier',
			'Février',
			'Mars',
			'Avril',
			'Mai',
			'Juin',
			'Juillet',
			'Août',
			'Septembre',
			'Octobre',
			'Novembre',
			'Décembre',
		];
		return monthNames[new Date().getMonth()];
	}

	getCurrentYearString() {
		return `${new Date().getFullYear()}`;
	}

	getCurrentDayInMonth(): number {
		return new Date().getDate();
	}

	getTotalDaysInCurrentMonth(): number {
		const today = new Date();
		return new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
	}

	getDayInYear(): number {
		const today = new Date();
		return Math.ceil(
			(today.valueOf() - new Date(today.getFullYear(), 0, 1).valueOf()) /
				86400000
		);
	}

	// HELPERS
	private isIsoDateString(isoString: string): RegExpExecArray | null | boolean {
		return /(\d{4}-\d{2}-\d{2})[A-Z]+(\d{2}:\d{2}:\d{2}).([0-9-:]+[A-Z])/.test(
			isoString
		);
	}
}

export default DateTimeService;
